import { useCallback, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { logOut, tokenRefreshed } from '../actions';
import { b2cPolicies, petCloudScope, webviewScope } from '../authConfigB2C';
import { getData, removeAllData, saveData } from '../services/api';
import {
  API_TOKEN,
  IMPERSONATE_KEY,
  WEBVIEW_API_TOKEN,
} from '../services/constants';

const SLEEP_TIME = 2000;

const useB2CInfo = () => {
  const { accounts, instance } = useMsal();
  const dispatch = useDispatch();
  const store = useSelector(({ session }) => ({ session }), shallowEqual);
  const [sleepTime, setSleepTime] = useState(0);

  const logoutB2C = useCallback(async () => {
    const postLogoutRedirectUri = window.location.origin;
    const b2cAuthority = b2cPolicies.authorities.signUpSignIn.authority;
    const logoutRequest = {
      account: instance.getActiveAccount(),
      authority: b2cAuthority,
      postLogoutRedirectUri,
    };

    dispatch(logOut());
    removeAllData();

    instance.logoutRedirect(logoutRequest);
  }, [instance, store.session.isImpersonate]);

  const tokenRequestB2C = useCallback(async ({
    authority = b2cPolicies.authorities.signUpSignIn.authority,
    scopes = [],
    tokenKey = '',
  }) => {
    let apiToken;

    try {
      const apiResponse = await instance.acquireTokenSilent({
        account: accounts[0],
        authority,
        forceRefresh: true,
        scopes,
      });

      apiToken = apiResponse?.accessToken || apiResponse?.idToken;

      saveData(tokenKey, apiToken);
    } catch (error) {
      // session or token end

      logoutB2C();
    }

    return apiToken;
  }, [accounts, instance]);

  const getAllTokens = useCallback(async () => {
    if (sleepTime > 0) {
      // skip many refresh token request
      dispatch(tokenRefreshed());
      return;
    }

    setSleepTime(SLEEP_TIME);

    const impersonate = Boolean(getData(IMPERSONATE_KEY));

    const authority = impersonate
      ? b2cPolicies.authorities.impersonation.authority
      : b2cPolicies.authorities.signUpSignIn.authority;

    const hasToken = await tokenRequestB2C({
      authority,
      scopes: [petCloudScope],
      tokenKey: API_TOKEN,
    });

    if (!hasToken) {
      return;
    }

    await tokenRequestB2C({
      authority,
      scopes: [webviewScope],
      tokenKey: WEBVIEW_API_TOKEN,
    });

    setSleepTime(0);
    dispatch(tokenRefreshed());
  }, [instance, sleepTime, tokenRequestB2C]);

  // webview screens still rely on a dedicated proxy-scoped token
  const getProxyToken = useCallback(async () => {
    const impersonate = Boolean(getData(IMPERSONATE_KEY));

    const authority = impersonate
      ? b2cPolicies.authorities.impersonation.authority
      : b2cPolicies.authorities.signUpSignIn.authority;

    const apiToken = await tokenRequestB2C({
      authority,
      scopes: [petCloudScope],
      tokenKey: API_TOKEN,
    });

    if (!apiToken) {
      return null;
    }

    const proxyToken = await tokenRequestB2C({
      authority,
      scopes: [webviewScope],
      tokenKey: WEBVIEW_API_TOKEN,
    });

    return proxyToken;
  }, [instance, tokenRequestB2C]);

  const forgotPasswordB2C = useCallback(() => {
    window.location.replace(b2cPolicies.authorities.forgotPassword.redirectUrl);
  }, [window.location]);

  return {
    forgotPasswordB2C,
    getAllTokens,
    getProxyToken,
    logoutB2C,
    tokenRequestB2C,
  };
};

export { useB2CInfo };
